/**
 * IconCategoryTabs Component
 *
 * Category tabs for filtering icons by type in the IconPicker.
 * Uses ARIA tablist pattern for accessibility.
 *
 * Features:
 * - Proper ARIA tablist/tab roles
 * - Visual pending state during category transitions
 * - Responsive flex-wrap layout
 */

import { cn } from '@/utils/classname';

import type { IconCategory } from './categories.config';

interface IconCategoryTabsProps {
  /** Available categories to display */
  categories: IconCategory[];
  /** Currently selected category id */
  selectedCategory: string;
  /** Callback when category is selected */
  onSelectCategory: (categoryId: string) => void;
  /** Whether a category transition is pending (non-blocking) */
  isPending: boolean;
}

/**
 * Category tabs with proper ARIA attributes.
 *
 * Uses tablist/tab pattern with aria-selected for screen readers.
 *
 * @example
 * ```tsx
 * <IconCategoryTabs
 *   categories={ICON_CATEGORIES}
 *   selectedCategory={selectedCategory}
 *   onSelectCategory={handleCategoryChange}
 *   isPending={isPending}
 * />
 * ```
 */
export function IconCategoryTabs({
  categories,
  selectedCategory,
  onSelectCategory,
  isPending,
}: IconCategoryTabsProps) {
  return (
    <div
      role="tablist"
      aria-label="Icon categories"
      className="flex flex-wrap gap-2"
      data-testid="icon-category-tabs"
    >
      {categories.map(category => (
        <button
          key={category.id}
          role="tab"
          aria-selected={selectedCategory === category.id}
          aria-controls={`panel-${category.id}`}
          id={`tab-${category.id}`}
          onClick={() => onSelectCategory(category.id)}
          className={cn(
            'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
            'focus-visible:ring-chariot-stroke-focused focus:outline-hidden focus-visible:ring-2',
            selectedCategory === category.id
              ? 'bg-chariot-brand-primary text-white'
              : 'bg-chariot-background-secondary text-chariot-text-secondary hover:bg-chariot-background-tertiary',
            isPending && 'opacity-70'
          )}
          data-testid={`icon-category-tab-${category.id}`}
        >
          {category.label}
        </button>
      ))}
    </div>
  );
}
